import { c as _c } from "react/compiler-runtime";
import { BasicStore, DetailedStore, type DetailedStoreWithDistance, Location } from '../interfaces';
import React, { Dispatch, FormEvent, ReactElement, SetStateAction, memo, startTransition, useCallback, useEffect, useRef, useState } from 'react';
import { STORE_QUERY_PARAM, getStoreQuerySearch, sanitizeStoreSearchTitle } from '@spa/lib/storeSlug';
import { UseUserLocation, useUserLocation } from '@spa/lib/react/hooks/useUserLocation';
import { usePathname, useRouter } from 'next/navigation';
import ImageComponent from '@spa/components/image-component/image.component';
import MapCardComponent from '@spa/components/map-card/map-card.component';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import PlaceholderComponent from '@spa/components/placeholder/placeholder.component';
import type { ProductInfoProduct } from '@spa/components/product-info/ProductInfoProductTypes';
import StoreFilterComponent from '../store-filter/store-filter.component';
import { StoreSearchProductInfoComponent } from './store-search-product-info.component';
import StoreTileComponent from '../store-tile/store-tile.component';
import styles from './store-search.component.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useObjectReducer from '@spa/lib/react/hooks/useObjectReducer';
import { useStoresAPI } from '@spa/lib/react/hooks/useStoresAPI';
const INITIAL_SEARCH_RESULTS_PLACEHOLDER_HEIGHT = 591;
export interface StoreSearchComponentProps {
  readonly allBasicStores: ReadonlyArray<BasicStore>;
  readonly centerMapToSearchResults: (location: Location, stores: ReadonlyArray<DetailedStoreWithDistance>) => void;
  readonly id: string;
  readonly isLoadingInitialStores: boolean;
  readonly isHomepage: boolean;
  /** Called once the search hydrated from the mount URL (?storeQuery=) has resolved. */
  readonly onHydratedSearchSettled?: () => void;
  readonly searchResultStores: ReadonlyArray<DetailedStoreWithDistance>;
  readonly selectedStoreCode: string | null;
  readonly setShownBasicStores: Dispatch<SetStateAction<ReadonlyArray<BasicStore>>>;
  readonly setSearchResultStores: Dispatch<SetStateAction<ReadonlyArray<DetailedStoreWithDistance>>>;
  readonly setSelectedStore: (newSelectedStore: DetailedStore) => void;
  readonly setShownStore: Dispatch<SetStateAction<DetailedStore | null>>;
  readonly setCenterUserLocation: Dispatch<SetStateAction<boolean>>;
  readonly shownStore: DetailedStore | null;
  readonly product?: ProductInfoProduct;
}
function StoreSearchComponent(t0) {
  const $ = _c(72);
  const {
    allBasicStores,
    centerMapToSearchResults,
    id,
    isLoadingInitialStores,
    isHomepage,
    onHydratedSearchSettled,
    searchResultStores,
    selectedStoreCode,
    shownStore,
    setShownBasicStores,
    setShownStore,
    setSearchResultStores,
    setSelectedStore,
    setCenterUserLocation,
    product
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      activeSearchTerm: "",
      geolocationError: false,
      prevSearchQuery: "",
      searchQuery: "",
      showSearchResultMessage: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [state, updateState] = useObjectReducer(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [selectedAssortments, setSelectedAssortments] = useState(t2);
  const {
    getUserLocation
  } = useUserLocation();
  const {
    getGeolocation,
    getClosestDetailedStores
  } = useStoresAPI();
  const {
    formatMessage
  } = useNamespacedIntl("storefinder");
  const router = useRouter();
  const pathname = usePathname();
  const hasHydratedRef = useRef(false);
  const defaultTitleRef = useRef(null);
  const {
    activeSearchTerm,
    geolocationError,
    prevSearchQuery,
    searchQuery,
    showSearchResultMessage
  } = state;
  const shouldShowInitialResultsPlaceholder = isLoadingInitialStores && searchResultStores.length === 0;
  let t3;
  if ($[2] !== updateState) {
    t3 = event => {
      updateState({
        searchQuery: event.target.value
      });
    };
    $[2] = updateState;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleSearchChange = t3;
  let t4;
  if ($[4] !== centerMapToSearchResults || $[5] !== getClosestDetailedStores || $[6] !== getGeolocation || $[7] !== setSearchResultStores || $[8] !== updateState) {
    t4 = async term => {
      updateState({
        geolocationError: false,
        prevSearchQuery: `- ${term}`,
        showSearchResultMessage: false
      });
      startTransition(() => {
        setSearchResultStores([]);
      });
      await getGeolocation(term).then(async geolocation => {
        if (geolocation) {
          const detailedStoresWithDistance = await getClosestDetailedStores(geolocation.lat, geolocation.lng);
          startTransition(() => {
            setSearchResultStores(detailedStoresWithDistance);
            centerMapToSearchResults(geolocation, detailedStoresWithDistance);
          });
        }
      }).catch(() => {
        updateState({
          geolocationError: true
        });
      });
      startTransition(() => {
        updateState({
          showSearchResultMessage: true
        });
      });
    };
    $[4] = centerMapToSearchResults;
    $[5] = getClosestDetailedStores;
    $[6] = getGeolocation;
    $[7] = setSearchResultStores;
    $[8] = updateState;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const runSearch = t4;
  let t5;
  if ($[10] !== formatMessage) {
    t5 = term_0 => {
      if (typeof document === "undefined") {
        return;
      }
      if (defaultTitleRef.current === null) {
        defaultTitleRef.current = document.title;
      }
      const sanitizedTerm = sanitizeStoreSearchTitle(term_0);
      document.title = formatMessage("searchPageTitle", {
        searchTerm: sanitizedTerm
      }, sanitizedTerm);
    };
    $[10] = formatMessage;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const updateTitle = t5;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      if (typeof document !== "undefined" && defaultTitleRef.current !== null) {
        document.title = defaultTitleRef.current;
      }
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const restoreTitle = t6;
  let t7;
  if ($[13] !== pathname || $[14] !== router) {
    t7 = term_1 => {
      router.replace(`${pathname}${getStoreQuerySearch(term_1)}`, {
        scroll: false
      });
    };
    $[13] = pathname;
    $[14] = router;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const writeSearchToUrl = t7;
  let t8;
  if ($[16] !== pathname || $[17] !== router) {
    t8 = () => {
      router.replace(pathname, {
        scroll: false
      });
    };
    $[16] = pathname;
    $[17] = router;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const clearSearchUrl = t8;
  let t9;
  if ($[19] !== isHomepage || $[20] !== runSearch || $[21] !== searchQuery || $[22] !== updateState || $[23] !== updateTitle || $[24] !== writeSearchToUrl) {
    t9 = async event_0 => {
      event_0.preventDefault();
      if (searchQuery.length > 0) {
        if (isHomepage) {
          startTransition(() => {
            updateState({
              activeSearchTerm: searchQuery
            });
          });
          writeSearchToUrl(searchQuery);
          updateTitle(searchQuery);
        }
        await runSearch(searchQuery);
      }
    };
    $[19] = isHomepage;
    $[20] = runSearch;
    $[21] = searchQuery;
    $[22] = updateState;
    $[23] = updateTitle;
    $[24] = writeSearchToUrl;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const handleSearchSubmit = t9;
  let t10;
  let t11;
  if ($[26] !== isHomepage || $[27] !== onHydratedSearchSettled || $[28] !== runSearch || $[29] !== updateState || $[30] !== updateTitle) {
    t10 = () => {
      if (hasHydratedRef.current) {
        return;
      }
      hasHydratedRef.current = true;
      if (!isHomepage || typeof window === "undefined") {
        return;
      }
      const urlSearchTerm = new URLSearchParams(window.location.search).get(STORE_QUERY_PARAM);
      if (urlSearchTerm !== null && urlSearchTerm.trim().length > 0) {
        updateState({
          activeSearchTerm: urlSearchTerm,
          searchQuery: urlSearchTerm
        });
        updateTitle(urlSearchTerm);
        runSearch(urlSearchTerm).finally(() => {
          onHydratedSearchSettled?.();
        });
      }
    };
    t11 = [isHomepage, onHydratedSearchSettled, runSearch, updateState, updateTitle];
    $[26] = isHomepage;
    $[27] = onHydratedSearchSettled;
    $[28] = runSearch;
    $[29] = updateState;
    $[30] = updateTitle;
    $[31] = t10;
    $[32] = t11;
  } else {
    t10 = $[31];
    t11 = $[32];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => () => {
      restoreTitle();
    };
    t13 = [restoreTitle];
    $[33] = t12;
    $[34] = t13;
  } else {
    t12 = $[33];
    t13 = $[34];
  }
  useEffect(t12, t13);
  let t14;
  if ($[35] !== formatMessage || $[36] !== geolocationError || $[37] !== prevSearchQuery || $[38] !== searchResultStores) {
    t14 = () => {
      let message;
      if (geolocationError) {
        message = formatMessage("geolocationError");
      } else {
        if (searchResultStores.length === 0) {
          message = formatMessage("noResults", {
            searchTerm: prevSearchQuery
          });
        } else {
          message = formatMessage("storesNearYou", {
            numberOfStores: searchResultStores.length,
            searchTerm: prevSearchQuery
          });
        }
      }
      return message;
    };
    $[35] = formatMessage;
    $[36] = geolocationError;
    $[37] = prevSearchQuery;
    $[38] = searchResultStores;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  const getSearchResultMessage = t14;
  let t15;
  if ($[40] !== selectedStoreCode) {
    t15 = storeCode => storeCode === selectedStoreCode;
    $[40] = selectedStoreCode;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  const isSelectedStore = t15;
  let t16;
  if ($[42] !== centerMapToSearchResults || $[43] !== clearSearchUrl || $[44] !== getClosestDetailedStores || $[45] !== getUserLocation || $[46] !== isHomepage || $[47] !== setCenterUserLocation || $[48] !== setSearchResultStores || $[49] !== updateState) {
    t16 = async () => {
      updateState({
        geolocationError: false,
        prevSearchQuery: "",
        searchQuery: ""
      });
      startTransition(() => {
        updateState({
          activeSearchTerm: ""
        });
      });
      if (isHomepage) {
        clearSearchUrl();
        restoreTitle();
      }
      try {
        const userLocation = await getUserLocation();
        setCenterUserLocation(true);
        const detailedStoresWithDistance_0 = await getClosestDetailedStores(userLocation.lat, userLocation.lng);
        startTransition(() => {
          setSearchResultStores(detailedStoresWithDistance_0);
          centerMapToSearchResults(userLocation, detailedStoresWithDistance_0);
        });
      } catch {
        updateState({
          geolocationError: true
        });
      }
    };
    $[42] = centerMapToSearchResults;
    $[43] = clearSearchUrl;
    $[44] = getClosestDetailedStores;
    $[45] = getUserLocation;
    $[46] = isHomepage;
    $[47] = setCenterUserLocation;
    $[48] = setSearchResultStores;
    $[49] = updateState;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  const handleUseUserLocation = t16;
  let t17;
  if ($[51] !== activeSearchTerm || $[52] !== allBasicStores || $[53] !== formatMessage || $[54] !== getSearchResultMessage || $[55] !== handleSearchChange || $[56] !== handleSearchSubmit || $[57] !== handleUseUserLocation || $[58] !== id || $[59] !== isHomepage || $[60] !== isSelectedStore || $[61] !== product || $[62] !== searchQuery || $[63] !== searchResultStores || $[64] !== selectedAssortments || $[65] !== setSelectedStore || $[66] !== setShownBasicStores || $[67] !== setShownStore || $[68] !== shouldShowInitialResultsPlaceholder || $[69] !== showSearchResultMessage || $[70] !== shownStore) {
    t17 = <div className={styles["store-search"]}>{!isHomepage && shownStore ? <MapCardComponent isProductAvailability={product !== undefined} isHomepage={isHomepage} isSelectedStore={isSelectedStore(shownStore.code)} product={product ?? null} store={shownStore} setShownStore={setShownStore} setSelectedStore={setSelectedStore} /> : <><div className={styles["store-search__headline"]}><span className="h3 bold">{formatMessage("chooseStore")}</span>{!isHomepage && <span className="text-s"> {formatMessage("noOnlineBookingNow")}</span>}</div><span className="text-lg">{formatMessage("searchInfo")}</span><div className={styles["store-search__searchbar-row"]}><form onSubmit={handleSearchSubmit} className={styles["store-search__search-form"]}><div className={styles["store-search__input-wrapper"]}><input type="search" placeholder={formatMessage("searchPlaceHolder")} value={searchQuery} onChange={handleSearchChange} className={styles["store-search__search-input"]} /></div><button type="submit" title={formatMessage("search")} className={styles["store-search__btn-wrapper"]}><ImageComponent className={styles["store-search__icon"]} src="/assets/icons/search-white.svg" alt={`${formatMessage("search")} icon`} width={20} height={20} /></button></form><button type="button" title={formatMessage("location")} className={styles["store-search__btn-wrapper"]} onClick={handleUseUserLocation}><ImageComponent className={styles["store-search__icon"]} src="/assets/icons/my-location.svg" alt={`${formatMessage("search")} icon`} width={20} height={20} /></button></div>{product && <StoreSearchProductInfoComponent product={product} />}{showSearchResultMessage && <div className={styles["store-search__search-message"]}>{getSearchResultMessage()}</div>}<div className={styles["store-search__search-results"]}>{!product && <StoreFilterComponent allBasicStores={allBasicStores} setShownBasicStores={setShownBasicStores} selectedAssortments={selectedAssortments} setSelectedAssortments={setSelectedAssortments} />}{shouldShowInitialResultsPlaceholder ? <PlaceholderComponent id={`${id}-initial-results-placeholder`} heightSinceWidth={[{
            sinceWidth: 0,
            height: INITIAL_SEARCH_RESULTS_PLACEHOLDER_HEIGHT
          }]} /> : searchResultStores.map(store => <StoreTileComponent key={store.code} store={store} isHomepage={isHomepage} isSelectedStore={isSelectedStore(store.code)} productCode={product?.code ?? null} storefinderSearchTerm={activeSearchTerm} setSelectedStore={setSelectedStore} setShownStore={setShownStore} />)}</div></>}</div>;
    $[51] = activeSearchTerm;
    $[52] = allBasicStores;
    $[53] = formatMessage;
    $[54] = getSearchResultMessage;
    $[55] = handleSearchChange;
    $[56] = handleSearchSubmit;
    $[57] = handleUseUserLocation;
    $[58] = id;
    $[59] = isHomepage;
    $[60] = isSelectedStore;
    $[61] = product;
    $[62] = searchQuery;
    $[63] = searchResultStores;
    $[64] = selectedAssortments;
    $[65] = setSelectedStore;
    $[66] = setShownBasicStores;
    $[67] = setShownStore;
    $[68] = shouldShowInitialResultsPlaceholder;
    $[69] = showSearchResultMessage;
    $[70] = shownStore;
    $[71] = t17;
  } else {
    t17 = $[71];
  }
  return t17;
}
export default memo(StoreSearchComponent);